import React, { useContext } from 'react';
import Breadcrumbs from '../../components/Breadcrumbs';
import ConfirmButton from '../../components/common/ConfirmButton';
import Header from '../../components/common/Header';
import Signature from '../../components/signature/Signature';
import { Context } from '../../context/Context';
import { AppContext, PhotoSection, Brands } from '../../context/interfaces';
import { car, pinIcon, genResumeEmail } from '../../context/Utils';
import ProgressBar from '../../components/ProgressBar';
import SectionDetail from '../../customHooks/summary/sectionDetail';
import { BRAND, getCurrentClaimantKey, urlIdRewrite } from 'shared/utils';
import HeliosClient from '../../context/Helios';
import CpeClient from '../../context/CPE';
import { completeSectionGTM } from 'shared/utils/gooogleTagManager';
import { useLocation, useParams } from 'react-router';

import '../../assets/styles/screens/summary/Summary.scss';

interface SummaryProps {
	to: string;
	screenIndex: number;
}

const SummaryScreen = ({ to, screenIndex }: SummaryProps): JSX.Element => {
	const context = useContext(Context) as AppContext;
	const { photos, location, signature, ClaimantType } = context;
	const { originalAddress: address } = location;
	const { id } = useParams<{ id: string }>();
	const locationPath = useLocation();
	const existsCounterpart = localStorage.getItem('existsCounterpart')
		? String(localStorage.getItem('existsCounterpart'))
		: '1';

	const awsUrlsByCategory = Object.fromEntries(
		Object.entries(PhotoSection)
			.filter(([key]) => !isNaN(Number(key)))
			.map(([key]) => [Number(key), [] as string[]])
	) as Record<PhotoSection, string[]>;

	photos.forEach((p) => {
		if (p.awsUrl) {
			awsUrlsByCategory[p.section].push(p.awsUrl || '');
		}
	});

	const canContinue = !!signature;

	const stateFlowKey = getCurrentClaimantKey(BRAND as Brands, ClaimantType);

	const handleFinishService = async (): Promise<void> => {
		try {
			let serviceId = localStorage.getItem('ServiceId');

			if (!serviceId && id) {
				const data = await new HeliosClient().getInspection(id, context);
				serviceId = data?.serviceId;
			}

			if (serviceId) {
				const data = {
					logMessage: 'Finished service by IV.',
					mongoServiceId: serviceId,
					serviceId: serviceId,
					_id: serviceId,
				};

				const response = await new HeliosClient().finishService(data);
				console.log('Service finished:', response);
			} else {
				console.warn('Ningún service Id asociado para finalizar');
			}
		} catch (error) {
			console.error('Error finishing service:', error);
		}
	};

	const handleFinalize = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
		if (!canContinue) {
			event.preventDefault();
			return;
		}

		// Complete inspection via Helios API
		completeSectionGTM(context, locationPath.pathname);
		new HeliosClient().finishInspection(context, id);

		// Complete inspection via CPE API if applicable
		if (context.cpeId && context.cpeInspectionId && context.cpeInspectionIsSync === true) {
			new CpeClient().completeInspection(context.cpeId, context.cpeInspectionId);
		}

		// Finish service for PR brands
		if (
			BRAND === Brands.PREMIER ||
			BRAND === Brands.OPTIMA ||
			BRAND === Brands.MULTINATIONAL ||
			BRAND === Brands.SODA
		) {
			handleFinishService();
		}

		// Send resume email
		const eventRecord = JSON.parse(localStorage.getItem('state') || '{}').eventId;
		genResumeEmail(eventRecord);
	};

	return (
		<div className="summary-screen">
			<Breadcrumbs currentStep={screenIndex} />
			<ProgressBar screenIndex={screenIndex} />
			<Header title="Resumen de la inspección" subtitle="" />
			<div className="container">
				<div className="summary-row">
					<img src={pinIcon} className="icon" alt="" />
					<div className="description">
						<div>Ubicación de accidente</div>
						<div className="subtext">{address}</div>
					</div>
				</div>
				<div className="summary-row">
					<img src={car} className="icon" alt="" />
					<div className="description">
						<div>
							Vehículos en accidente:
							{existsCounterpart}
						</div>
					</div>
				</div>
				<SectionDetail awsUrlsByCategory={awsUrlsByCategory} stateKey={stateFlowKey} />
				<h1>Firma</h1>
				<Signature />
			</div>
			<ConfirmButton text="Finalizar" to={urlIdRewrite(to, id)} disabled={!canContinue} onClick={handleFinalize} />
		</div>
	);
};

export default SummaryScreen;
